'use client'

import React, { useCallback, useMemo, useRef } from 'react'
import { cn } from 'app/lib/utils'
import PageFeedback from '../PageFeedback/PageFeedback'
import DocsPrevNext from '../DocsPrevNext/DocsPrevNext'
import TableOfContents from '../DocsTOC/DocsTOC'
import OpenInAI from '@/components/OpenInAI'
import TagsWithTooltips from '@/components/TagsWithTooltips/TagsWithTooltips'
import { usePathname } from 'next/navigation'
import { buildCopyMarkdownFromRendered } from '@/utils/docs/buildCopyMarkdownFromRendered'
import type { RelatedPageLinksEntry } from '@/utils/relatedPageLinks'
import { RelatedPageLinks } from '@/components/RelatedPageLinks/RelatedPageLinks'
import { isDocsOnboardingPathname } from '@/utils/docs/onboardingPath'
import { resolveLatestDate, formatDisplayDate } from '@/utils/dateUtils'
import Breadcrumb from '@/components/Breadcrumb/Breadcrumb'
import type { BreadcrumbCrumb } from '@/utils/breadcrumbTypes'

const DocContent: React.FC<{
  title: string
  relatedPageLinks?: RelatedPageLinksEntry | null
  post: any
  toc: any
  hideTableOfContents: boolean
  breadcrumbs?: BreadcrumbCrumb[]
  children: React.ReactNode
}> = ({ title, relatedPageLinks, post, toc, hideTableOfContents, breadcrumbs, children }) => {
  const pathname = usePathname()
  const lastUpdatedDate = post?.lastmod || resolveLatestDate(post)
  const formattedDate = formatDisplayDate(lastUpdatedDate)
  const isOnboarding = isDocsOnboardingPathname(pathname)
  // Check if this is the introduction page (exclude copy functionality)
  const isIntroductionPage = post.slug === 'introduction'

  const hasTabs = !!post?.body?.raw && post.body.raw.includes('<Tabs')
  const effectiveHideTOC = hideTableOfContents && !hasTabs
  const shouldRenderTOC = !effectiveHideTOC && Array.isArray(toc) && toc.length > 0 && !isOnboarding
  const shouldReserveTocColumn = !isOnboarding
  const articleRef = useRef<HTMLElement | null>(null)

  const docTags = useMemo(() => post?.docTags || [], [post?.docTags])

  const fallbackMarkdown = useMemo(() => {
    const tagLine = docTags.length > 0 ? `Tags: ${docTags.join(', ')}` : ''
    return [`# ${title}`, tagLine, post?.body?.raw || ''].filter(Boolean).join('\n\n')
  }, [docTags, post?.body?.raw, title])

  const getMarkdownContent = useCallback(async () => {
    if (!articleRef.current) {
      return fallbackMarkdown
    }
    return buildCopyMarkdownFromRendered(articleRef.current, {
      title,
      relatedPageLinks,
      tags: docTags,
      includeTagDefinitions: true,
    })
  }, [docTags, fallbackMarkdown, relatedPageLinks, title])

  return (
    <>
      <div
        data-docs-content-column=""
        className={`box-border min-w-0 flex-[1_1_auto] [&_details+details]:mt-8 ${isOnboarding ? 'w-full px-4' : ''}`}
      >
        {breadcrumbs && !isOnboarding && <Breadcrumb crumbs={breadcrumbs} />}
        <div
          className={cn(
            'm-0 flex items-center justify-between gap-2',
            relatedPageLinks && 'relative'
          )}
        >
          <div className="flex flex-col items-start gap-2">
            <h1 className="mt-2 text-3xl leading-tight">{title}</h1>
          </div>
          {!isIntroductionPage && post.body?.raw && (
            <div className="flex shrink-0 items-center gap-2">
              <RelatedPageLinks entry={relatedPageLinks} />
              <div data-listicle-directory-search-slot="" className="empty:hidden" />
              <OpenInAI
                getMarkdownContent={getMarkdownContent}
                pageUrl={pathname}
                className="shrink-0"
                copyLabel="Copy markdown"
                docSlug={post.slug}
              />
            </div>
          )}
        </div>
        {!isOnboarding && post.docTags && post.docTags.length > 0 && (
          <TagsWithTooltips tags={post.docTags} />
        )}
        <article ref={articleRef} className="prose prose-slate max-w-none py-6 dark:prose-invert">
          {children}
        </article>
        {/* Mobile / no-TOC: feedback → last updated, above prev/next */}
        <div className={cn('mt-8 flex flex-col gap-6', shouldReserveTocColumn && 'lg:hidden')}>
          <PageFeedback />
          {formattedDate && (
            <p className="m-0 text-sm font-medium text-[var(--l2-foreground)]">
              Last updated
              <span className="mx-1">—</span>
              <span className="text-[var(--l1-foreground-hover)]">{formattedDate}</span>
            </p>
          )}
        </div>
        <DocsPrevNext />
      </div>

      {shouldReserveTocColumn ? (
        <TableOfContents
          toc={shouldRenderTOC ? toc : []}
          hideTableOfContents={false}
          source=""
          formattedDate={formattedDate || undefined}
        />
      ) : null}
    </>
  )
}

export default DocContent
